import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { actions } from 'store/reports';
import { getUnsubscribeCount } from 'store/unSubscribe/actions';
import queryValidator from './validator';
import FormController from 'common/formController';

import get from 'lodash.get';
import moment from 'moment';
import { getCampaignDataStorePath } from 'store/reports';

import {
  convertToDateOnlyString,
  convertToDateTimeFormat2OnlyString
} from 'helpers/timeUtils';
import {
  types as emailStatBoxTypes,
  smsTypes as smsStatBoxTypes
} from './constants';
import { getEnvVar } from 'helpers';

const {
  getContextReportData,
  getCampaignReportData,
  getCampaignReportCsvFile,
  downloadedCampaignReportCsvFile,
  setReportQueryData,
  getEmailClickMetrics
} = actions;

export default function useReport(options = {}) {
  const {
    shouldFetchUnsubscribeCount = true,
    isActivityView = false,
    ignoreCampaignListUpdates = false,
    suppressContextFetch = false
  } = options;
  const dispatch = useDispatch();

  const isReportsGetting = useSelector(store => store.reports.getting) || false;
  const queryParamDataStore = useSelector(store => store.reports.queryData);
  const contextReportDataStore = useSelector(
    store => store.reports.contextReportData
  );
  const campaignReportDataStore = useSelector(
    store => store.reports.campaignReportData
  );
  const campaignReportDataCountStore = useSelector(
    store => store.reports.campaignReportDataCount
  );
  const unsubscribeCount = useSelector(store => store.unSubscribe.unsubscribeCount);
  const error = useSelector(store => store.reports.error);
  const campaignsList = useSelector(store => store.campaign.advancedPaginatedList);
  const { campaignUuid, type, providerMetric } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState({});
  const [campaignData, setcampaignData] = useState([]);
  const [activeCampaignsList, setActiveCampaignsList] = useState([]);
  const [campaignDataCount, setcampaignDataCount] = useState(0);
  const [queryData, setQueryData] = useState({});
  const [queryDataErrors, setQueryDataErrors] = useState({});
  const hasInitializedCampaignListSyncRef = useRef(false);
  const hasHandledInitialCampaignAvailabilityRef = useRef(false);
  const lastDispatchedQueryKeyRef = useRef('');
  const lastContextFetchKeyRef = useRef('');
  const lastUnsubscribeFetchKeyRef = useRef('');
  const hasInitializedCampaignDateRangeRef = useRef(false);
  const expectedCampaignDateRangeRef = useRef(null);
  const dailyReportDelayHours =
    parseInt(getEnvVar('ADM_REPORT_DAILY_REPORT_DELAY_HOURS') || '4', 10) || 4;
  const monthlyReportDelayHours =
    parseInt(getEnvVar('ADM_REPORT_MONTHLY_REPORT_DELAY_HOURS') || '4', 10) || 4;
  const dailyRangeDays =
    parseInt(getEnvVar('ADM_REPORT_DAILY_MAX_RANGE_DAYS') || '60', 10) || 60;
  const monthlyRangeMonths =
    parseInt(getEnvVar('ADM_REPORT_MONTHLY_MAX_RANGE_MONTHS') || '24', 10) || 24;
  const dailyAggregationRuntimeCron =
    getEnvVar('ADM_REPORT_DAILY_AGGREGATION_RUNTIME') || '0 30 08 ? * *';
  const monthlyReportRuntimeCron =
    getEnvVar('ADM_REPORT_MONTHLY_REPORT_RUNTIME') || '0 30 14 2 * ?';

  const parseCronSchedule = (cronExpression, defaults) => {
    const parts = (cronExpression || '').trim().split(/\s+/);
    const toInt = (value, fallback) => {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? fallback : parsed;
    };

    return {
      minute: toInt(parts[1], defaults.minute),
      hour: toInt(parts[2], defaults.hour),
      dayOfMonth:
        parts[3] && parts[3] !== '?'
          ? toInt(parts[3], defaults.dayOfMonth)
          : defaults.dayOfMonth
    };
  };

  const dailyRuntime = parseCronSchedule(dailyAggregationRuntimeCron, {
    hour: 8,
    minute: 30,
    dayOfMonth: 1
  });
  const monthlyRuntime = parseCronSchedule(monthlyReportRuntimeCron, {
    hour: 14,
    minute: 30,
    dayOfMonth: 2
  });

  const gettingFileSucceeded = useSelector(store => store.reports.campaignReportCsv.gettingFileSucceeded);
  const gettingFile = useSelector(store => store.reports.campaignReportCsv.gettingFile);
  const isDownloading = useSelector(store => store.reports.campaignReportCsv.isDownloading);
  const campaignReportCsvBlob = useSelector(store => store.reports.campaignReportCsv.campaignReportCsvBlob);
  const selectedRowsPerPage = useSelector(store => store.user.selectedRowsPerPage || 10);
  const productNames = useSelector(
    store => store.events.supportedEventTypes.productNames
  );

  let formController = new FormController();

  const getMaxAvailableDailyDate = () => {
    const nowUtc = moment.utc();

    if (providerMetric || isActivityView) {
      return nowUtc.clone().startOf('day');
    }

    const dailyAggregationCompletionTime = nowUtc
      .clone()
      .startOf('day')
      .hour(dailyRuntime.hour)
      .minute(dailyRuntime.minute)
      .second(0)
      .millisecond(0)
      .add(dailyReportDelayHours, 'hours');

    // Keep cutoff source in UTC (cron + delay), but evaluate availability against local-day time.
    const localCompletionTemplate = dailyAggregationCompletionTime.clone().local();
    const nowLocal = moment();
    const dailyAggregationCompletionTimeLocal = nowLocal
      .clone()
      .startOf('day')
      .hour(localCompletionTemplate.hour())
      .minute(localCompletionTemplate.minute())
      .second(0)
      .millisecond(0);

    // If current time >= aggregation completion time, max available is yesterday (1 day back)
    // Otherwise, max available is day before yesterday (2 days back)
    const daysBack = nowLocal.isSameOrAfter(dailyAggregationCompletionTimeLocal) ? 1 : 2;

    return nowLocal.clone().subtract(daysBack, 'days').startOf('day');
  };

  const getMinAvailableDailyDate = () => {
    return getMaxAvailableDailyDate()
      .clone()
      .subtract(dailyRangeDays - 1, 'days')
      .startOf('day');
  };

  const getMaxAvailableMonthlyDate = () => {
    const nowUtc = moment.utc();

    const monthlyAggregationCompletionTime = nowUtc
      .clone()
      .startOf('month')
      .date(monthlyRuntime.dayOfMonth)
      .hour(monthlyRuntime.hour)
      .minute(monthlyRuntime.minute)
      .second(0)
      .millisecond(0)
      .add(monthlyReportDelayHours, 'hours');

    // If current time >= aggregation completion time, max available is 1 month back
    // Otherwise, max available is 2 months back
    const monthsBack = nowUtc.isSameOrAfter(monthlyAggregationCompletionTime) ? 1 : 2;

    return nowUtc
      .clone()
      .startOf('month')
      .subtract(monthsBack, 'months')
      .endOf('month');
  };

  const getMinAvailableMonthlyDate = () => {
    return getMaxAvailableMonthlyDate()
      .clone()
      .subtract(monthlyRangeMonths - 1, 'months')
      .startOf('month');
  };

  const validate = data => {
    const validation = queryValidator.validate(data || queryData, {
      abortEarly: false,
      allowUnknown: true // allow unknown keys that will be ignored
    });
    let errors = formController.getFormErrors(validation, {
      saveAttempted: true
    });
    setQueryDataErrors(errors);
    return errors;
  };

  const getDefaultDateRange = (dateType = 'DAY') => {
    if (dateType === 'MONTH') {
      const toDate = getMaxAvailableMonthlyDate().clone().endOf('month');
      const fromDate = toDate.clone().subtract(23, 'months').startOf('month');
      return {
        fromDate: fromDate.format('YYYY-MM-DD'),
        toDate: toDate.format('YYYY-MM-DD')
      };
    }

    const toDate = getMaxAvailableDailyDate().clone().startOf('day');
    const fromDate = toDate.clone().subtract(59, 'days').startOf('day');
    return {
      fromDate: fromDate.format('YYYY-MM-DD'),
      toDate: toDate.format('YYYY-MM-DD')
    };
  };

  useEffect(() => {
    if (
      ignoreCampaignListUpdates &&
      hasInitializedCampaignListSyncRef.current
    ) {
      return;
    }

    let activeCampaigns = campaignsList.filter(campaign => productNames.includes(campaign.productName));
    // Only update if the campaigns have actually changed
    if (JSON.stringify(activeCampaigns.map(c => c.uuid)) !== JSON.stringify(activeCampaignsList.map(c => c.uuid))) {
      setActiveCampaignsList(activeCampaigns);
    }

    if (ignoreCampaignListUpdates && campaignsList.length > 0) {
      hasInitializedCampaignListSyncRef.current = true;
    }
  }, [campaignsList, productNames]);

  useEffect(() => {
    if (!gettingFile) {
      if (gettingFileSucceeded && campaignReportCsvBlob.type === 'text/csv') {
        const url = window.URL.createObjectURL(new Blob([campaignReportCsvBlob]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `${campaignUuid}.csv`;
        link.click();
      }
      dispatch(downloadedCampaignReportCsvFile());
    }
  }, [gettingFileSucceeded]);

  useEffect(() => {
    const getInitialQueryData = currentQueryData => {
      // Activity view (providerMetric present) always uses DAY and fresh dates so the
      // initial state matches what setCampaignReportDates will later compute, preventing
      // a second dispatch with stale Details-page dates.
      const isActivityMount = Boolean(providerMetric);
      const resolvedDateType = isActivityMount ? 'DAY' : (currentQueryData?.dateType || 'DAY');
      const defaultRange = getDefaultDateRange(resolvedDateType);

      return {
        ...currentQueryData,
        dateType: resolvedDateType,
        fromDate: isActivityMount ? defaultRange.fromDate : (currentQueryData?.fromDate || defaultRange.fromDate),
        toDate: isActivityMount ? defaultRange.toDate : (currentQueryData?.toDate || defaultRange.toDate)
      };
    };

    let queryData = getInitialQueryData(queryParamDataStore || {});
    if (campaignUuid || queryData.campaignId) {
      queryData = {
        ...queryData,
        campaignUuid,
        providerMetric,
        type: type || queryData.type,
        ...(type === 'SMS' && { providerType: "VONAGE" })
      };
    }
    setQueryData({ ...queryData });
  }, []);

  const setUpdatedQueryData = (queryData, startDate, endDate) => {
    queryData = {
      ...queryData,
      fromDate: startDate,
      toDate: startDate <= endDate ? endDate : startDate,
      ...(queryData.type === 'SMS' && { providerType: "VONAGE" })
    };
    setQueryData({ ...queryData });
  };

  const setCampaignReportDates = queryData => {
    if (campaignUuid) {
      let currentCampaign = activeCampaignsList.filter(
        campaign => campaign.uuid === campaignUuid
      )[0];
      queryData = {
        ...queryData,
        campaignId: currentCampaign?.id || currentCampaign?.uuid,
        providerMetric,
        type: type || queryData.type
      };
      if (currentCampaign) {
        const defaultRange = getDefaultDateRange(queryData.dateType || 'DAY');
        expectedCampaignDateRangeRef.current = {
          fromDate: convertToDateOnlyString(defaultRange.fromDate),
          toDate: convertToDateOnlyString(defaultRange.toDate)
        };
        hasInitializedCampaignDateRangeRef.current = false;
        setUpdatedQueryData(
          queryData,
          defaultRange.fromDate,
          defaultRange.toDate
        );
      } else {
        hasInitializedCampaignDateRangeRef.current = false;
        expectedCampaignDateRangeRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (campaignUuid) {
      hasInitializedCampaignDateRangeRef.current = false;
      expectedCampaignDateRangeRef.current = null;
    }
  }, [campaignUuid]);

  useEffect(() => {
    if (activeCampaignsList.length > 0) {
      // Activity view always uses DAY — never inherit a MONTH dateType from the Redux
      // store, which would cause the dispatch effect to call updateParams('dateType','DAY')
      // and trigger a second API call.
      const resolvedDateType = providerMetric ? 'DAY' : (queryParamDataStore?.dateType || 'DAY');
      const defaultRange = getDefaultDateRange(resolvedDateType);

      let queryData = {
        ...queryParamDataStore,
        dateType: resolvedDateType,
        fromDate: providerMetric ? defaultRange.fromDate : (queryParamDataStore?.fromDate || defaultRange.fromDate),
        toDate: providerMetric ? defaultRange.toDate : (queryParamDataStore?.toDate || defaultRange.toDate)
      };
      setCampaignReportDates(queryData);
    }
  }, [activeCampaignsList, campaignUuid]);

  useEffect(() => {
    // Wait for fromDate and toDate to be set before dispatching
    if (
      Object.keys(queryData).length === 0 ||
      !queryData.fromDate ||
      !queryData.toDate ||
      (Object.keys(queryDataErrors).length > 0 && !queryDataErrors?.meta?.clean)
    ) {
      // Do nothing until date range is available and valid
      return;
    }

    const queryDispatchKey = JSON.stringify({
      campaignUuid,
      providerMetric,
      isActivityView,
      selectedRowsPerPage,
      queryData
    });
    const queryChanged = lastDispatchedQueryKeyRef.current !== queryDispatchKey;
    const shouldRunForInitialCampaignAvailability =
      !lastDispatchedQueryKeyRef.current &&
      !hasHandledInitialCampaignAvailabilityRef.current &&
      activeCampaignsList.length > 0;

    if (!queryChanged && !shouldRunForInitialCampaignAvailability) {
      return;
    }

    if (shouldRunForInitialCampaignAvailability) {
      hasHandledInitialCampaignAvailabilityRef.current = true;
    }

    if (queryChanged) {
      hasHandledInitialCampaignAvailabilityRef.current = true;
    }

    lastDispatchedQueryKeyRef.current = queryDispatchKey;

    if (campaignUuid) {
      let currentCampaign = activeCampaignsList.filter(
        campaign => campaign.uuid === campaignUuid
      )[0];
      queryData.campaignId = currentCampaign?.id || currentCampaign?.uuid;
      queryData.campaignName = currentCampaign?.name;
      queryData.campaignUuid = currentCampaign?.uuid;
      delete queryData.activeCampaignIds;
    } else {
      queryData.activeCampaignIds = activeCampaignsList
        .map(campaign => campaign.id || campaign.uuid);
      delete queryData.campaignId;
      delete queryData.campaignName;
      delete queryData.campaignUuid;
    }
    if (type) {
      queryData.type = type;
      if (type === 'SMS' && !queryData.providerType) {
        queryData.providerType = 'VONAGE';
      } else if (type === 'EMAIL') {
        delete queryData.providerType;
      }
    }
    if (isActivityView) {
      const resolvedProviderMetric = providerMetric || queryData.providerMetric;
      dispatch(setReportQueryData(queryData));

      if (resolvedProviderMetric) {
        if (queryData.dateType === 'MONTH') {
          updateParams('dateType', 'DAY');
          return;
        }

        dispatch(getCampaignReportData({ queryData, suppressUserMessage: true }));
      }

      // Fire email click metrics directly if all required values are present
      if (
        resolvedProviderMetric === 'CLICKED' &&
        queryData.campaignId &&
        queryData.fromDate &&
        queryData.toDate
      ) {
        dispatch(getEmailClickMetrics({
          campaignId: queryData.campaignId,
          fromDate: queryData.fromDate,
          toDate: queryData.toDate
        }));
      }
      return;
    }

    if (campaignUuid && !hasInitializedCampaignDateRangeRef.current) {
      const expectedRange = expectedCampaignDateRangeRef.current;
      if (expectedRange) {
        const currentFromDate = convertToDateOnlyString(queryData.fromDate);
        const currentToDate = convertToDateOnlyString(queryData.toDate);
        const expectedFromDate = convertToDateOnlyString(expectedRange.fromDate);
        const expectedToDate = convertToDateOnlyString(expectedRange.toDate);

        if (currentFromDate !== expectedFromDate || currentToDate !== expectedToDate) {
          return;
        }
      }

      hasInitializedCampaignDateRangeRef.current = true;
    }

    if (providerMetric) {
      if (queryData.dateType === 'MONTH') updateParams('dateType', 'DAY');
      else dispatch(getCampaignReportData({ queryData }));
    } else {
      if (suppressContextFetch) {
        return;
      }

      const contextFetchKey = JSON.stringify({
        campaignUuid,
        campaignId: queryData.campaignId || null,
        campaignUuidFromQuery: queryData.campaignUuid || null,
        type: queryData.type || null,
        providerType: queryData.providerType || null,
        dateType: queryData.dateType || null,
        fromDate: queryData.fromDate || null,
        toDate: queryData.toDate || null,
        dataHierarchy: queryData.dataHierarchy || null,
        activeCampaignIds: Array.isArray(queryData.activeCampaignIds)
          ? queryData.activeCampaignIds
          : []
      });

      if (lastContextFetchKeyRef.current === contextFetchKey) {
        return;
      }

      lastContextFetchKeyRef.current = contextFetchKey;
      dispatch(getContextReportData({ queryData, campaignUuid }));
    }
  }, [
    queryData,
    campaignUuid,
    activeCampaignsList,
    providerMetric,
    isActivityView,
    selectedRowsPerPage,
    suppressContextFetch
  ]);

  useEffect(() => {
    if (
      Object.keys(queryData).length > 0 &&
      queryData.providerMetric !== providerMetric
    ) {
      updateParams('providerMetric', providerMetric);
    }
  }, [providerMetric]);

  useEffect(() => {
    const isProviderMetricView = Boolean(providerMetric || queryData.providerMetric);

    if (
      shouldFetchUnsubscribeCount &&
      !isActivityView &&
      !suppressContextFetch &&
      (!campaignUuid || hasInitializedCampaignDateRangeRef.current) &&
      !isProviderMetricView &&
      Object.keys(queryData).length > 0 &&
      queryData.fromDate &&
      queryData.toDate &&
      queryData.type === 'EMAIL'
    ) {
      const unsubscribeCountPayload = {
        fromDate: queryData.fromDate,
        toDate: queryData.toDate
      };

      const selectedCampaignUuid = campaignUuid || queryData.campaignUuid;
      if (selectedCampaignUuid) {
        unsubscribeCountPayload.campaignUuids = [selectedCampaignUuid];
      }

      const unsubscribeFetchKey = JSON.stringify({
        fromDate: unsubscribeCountPayload.fromDate,
        toDate: unsubscribeCountPayload.toDate,
        type: queryData.type,
        campaignUuid: selectedCampaignUuid || null
      });

      if (lastUnsubscribeFetchKeyRef.current === unsubscribeFetchKey) {
        return;
      }

      lastUnsubscribeFetchKeyRef.current = unsubscribeFetchKey;

      dispatch(getUnsubscribeCount(unsubscribeCountPayload));
    }
  }, [
    shouldFetchUnsubscribeCount,
    isActivityView,
    suppressContextFetch,
    queryData.fromDate,
    queryData.toDate,
    queryData.campaignUuid,
    queryData.providerMetric,
    campaignUuid,
    providerMetric,
    queryData.type
  ]);

  useEffect(() => {
    let path = getCampaignDataStorePath(queryParamDataStore);
    if (path) {
      setReportData(get(contextReportDataStore, path));
    }
  }, [contextReportDataStore]);

  useEffect(() => {
    let addedDetails = setCampaignEmailReportDetails(campaignReportDataStore);
    setcampaignData(addedDetails);
    setcampaignDataCount(campaignReportDataCountStore);
  }, [campaignReportDataStore, campaignReportDataCountStore]);

  useEffect(() => {
    setIsLoading(isReportsGetting);
  }, [isReportsGetting]);

  const updateParams = (key, value) => {
    let params = { ...queryData };
    let normalizedValue = value;
    if (key === 'type') {
      if (value === 'SMS' && !params.providerType) {
        params.providerType = 'VONAGE';
      } else if (value === 'EMAIL') {
        delete params.providerType;
      }
    }
    if (key !== 'index') params['index'] = 0;
    if (key === 'dateType') {
      const defaultRange = getDefaultDateRange(value);
      params.fromDate = defaultRange.fromDate;
      params.toDate = defaultRange.toDate;
    }
    if (params.dateType === 'MONTH' && key === 'fromDate') {
      normalizedValue = moment(value).startOf('month').format('YYYY-MM-DD');
      params.toDate = moment(value).endOf('month').format('YYYY-MM-DD');
    }
    if (params.dateType === 'DAY' && key === 'toDate')
      params.fromDate = convertToDateOnlyString(params.fromDate);
    params[key] = normalizedValue;
    validate(params);
    setQueryData(params);
    if (key === 'dateType') {
      let queryData = { ...params };
      setCampaignReportDates(queryData);
    }
    if (key === 'providerMetric') {
      const hasDateRange = Boolean(params.fromDate && params.toDate);
      if (!hasDateRange) {
        setCampaignReportDates({ ...params });
      }
    }
  };

  const setByPath = (obj, path, value) => {
    if (typeof path === 'string') {
      path = path.split('.');
    }
    if (obj && path.length === 1) {
      obj[path] = value;
      return;
    }
    return setByPath(obj[path[0]], path.slice(1), value);
  };

  const handleDateChange = (newDate, fieldName) => {
    const nextDate = convertToDateOnlyString(newDate);

    if (!nextDate || !moment(nextDate).isValid()) {
      return false;
    }

    const currentDate = convertToDateOnlyString(queryData[fieldName]);

    if (nextDate === currentDate) {
      return false;
    }

    updateParams(fieldName, nextDate);
    return true;
  };

  const getPresetRange = (rangePreset, dateType = 'DAY') => {
    if (dateType === 'MONTH') {
      const maxMonth = getMaxAvailableMonthlyDate().clone().startOf('month');
      let fromMonth = maxMonth.clone().subtract(1, 'month').startOf('month');
      let toMonth = maxMonth.clone().subtract(1, 'month').endOf('month');
      const previousCalendarMonth = moment.utc().subtract(1, 'month').startOf('month');

      if (rangePreset === 'THIS_MONTH') {
        fromMonth = maxMonth.clone().startOf('month');
        toMonth = maxMonth.clone().endOf('month');
      }
      if (rangePreset === 'LAST_MONTH') {
        const targetMonth = previousCalendarMonth.isAfter(maxMonth, 'month')
          ? maxMonth.clone()
          : previousCalendarMonth.clone();
        fromMonth = targetMonth.clone().startOf('month');
        toMonth = targetMonth.clone().endOf('month');
      }
      if (rangePreset === 'LAST_2_MONTHS') {
        fromMonth = maxMonth.clone().subtract(1, 'month').startOf('month');
        toMonth = maxMonth.clone().endOf('month');
      }
      if (rangePreset === 'LAST_12_MONTHS') {
        fromMonth = maxMonth.clone().subtract(11, 'month').startOf('month');
        toMonth = maxMonth.clone().endOf('month');
      }
      if (rangePreset === 'LAST_24_MONTHS') {
        fromMonth = maxMonth.clone().subtract(23, 'month').startOf('month');
        toMonth = maxMonth.clone().endOf('month');
      }
      if (rangePreset === 'LAST_3_MONTHS') {
        fromMonth = maxMonth.clone().subtract(2, 'month').startOf('month');
        toMonth = maxMonth.clone().endOf('month');
      }
      if (rangePreset === 'LAST_6_MONTHS') {
        fromMonth = maxMonth.clone().subtract(5, 'month').startOf('month');
        toMonth = maxMonth.clone().endOf('month');
      }

      return {
        fromDate: fromMonth.format('YYYY-MM-DD'),
        toDate: toMonth.format('YYYY-MM-DD')
      };
    }

    const maxDate = getMaxAvailableDailyDate();
    let fromDate = maxDate.clone().subtract(6, 'days');
    let toDate = maxDate.clone();

    if (rangePreset === 'TODAY') {
      fromDate = maxDate.clone();
      toDate = maxDate.clone();
    }
    if (rangePreset === 'LAST_7_DAYS') {
      fromDate = maxDate.clone().subtract(6, 'days');
      toDate = maxDate.clone();
    }
    if (rangePreset === 'LAST_30_DAYS') {
      fromDate = maxDate.clone().subtract(29, 'days');
      toDate = maxDate.clone();
    }
    if (rangePreset === 'LAST_60_DAYS') {
      fromDate = maxDate.clone().subtract(59, 'days');
      toDate = maxDate.clone();
    }
    if (rangePreset === 'THIS_MONTH') {
      fromDate = maxDate.clone().startOf('month');
      toDate = maxDate.clone();
    }

    return {
      fromDate: fromDate.format('YYYY-MM-DD'),
      toDate: toDate.format('YYYY-MM-DD')
    };
  };

  const getMatchingPreset = (fromDate, toDate, dateType = 'DAY') => {
    if (!fromDate || !toDate) {
      return null;
    }

    const providedFromDate = moment(fromDate).format('YYYY-MM-DD');
    const providedToDate = moment(toDate).format('YYYY-MM-DD');

    // List of presets to check against, in order of preference
    const presetsToCheck = dateType === 'MONTH'
      ? ['LAST_24_MONTHS', 'LAST_12_MONTHS', 'LAST_6_MONTHS', 'LAST_3_MONTHS', 'LAST_2_MONTHS', 'LAST_MONTH', 'THIS_MONTH']
      : ['LAST_60_DAYS', 'LAST_30_DAYS', 'LAST_7_DAYS', 'TODAY', 'THIS_MONTH'];

    for (const preset of presetsToCheck) {
      const presetRange = getPresetRange(preset, dateType);
      if (presetRange.fromDate === providedFromDate && presetRange.toDate === providedToDate) {
        return preset;
      }
    }

    // If no preset matches, return null (user selected custom range)
    return null;
  };

  const applyFilters = nextFilters => {
    const params = {
      ...queryData,
      ...nextFilters
    };

    if (!Object.prototype.hasOwnProperty.call(nextFilters, 'index')) {
      params.index = 0;
    }

    if (params.dateType === 'MONTH') {
      if (params.fromDate) {
        params.fromDate = moment(params.fromDate)
          .startOf('month')
          .format('YYYY-MM-DD');
      }
      if (params.toDate) {
        params.toDate = moment(params.toDate)
          .endOf('month')
          .format('YYYY-MM-DD');
      }
    }

    if (params.dateType === 'DAY') {
      if (params.fromDate) {
        params.fromDate = moment(params.fromDate).format('YYYY-MM-DD');
      }
      if (params.toDate) {
        params.toDate = moment(params.toDate).format('YYYY-MM-DD');
      }
    }

    validate(params);
    setQueryData(params);
  };

  const getEmailStats = campaignReportData => {
    let statBoxesEmail = [
      {
        currentStat: campaignReportData?.totalMetrics?.totalRequested?.totalCount,
        statType: emailStatBoxTypes.REQUESTED
      },
      {
        currentStat:
          campaignReportData?.totalMetrics?.undeliveredMetrics?.totalAccepted
            ?.totalCount,
        statType: emailStatBoxTypes.ACCEPTED
      },
      {
        currentStat: campaignReportData?.totalMetrics?.totalDelivered?.totalCount,
        statType: emailStatBoxTypes.DELIVERED
      },
      {
        currentStat: campaignReportData?.totalMetrics?.totalOpened?.totalCount,
        statType: emailStatBoxTypes.OPENED
      },
      {
        currentStat: campaignReportData?.totalMetrics?.totalClicked?.totalCount,
        statType: emailStatBoxTypes.CLICKED
      },
      {
        currentStat:
          campaignReportData?.totalMetrics?.undeliveredMetrics?.totalBounced
            ?.totalCount,
        statType: emailStatBoxTypes.BOUNCED
      },
      {
        currentStat:
          campaignReportData?.totalMetrics?.undeliveredMetrics?.totalBlocked
            ?.totalCount,
        statType: emailStatBoxTypes.BLOCKED
      },
      {
        currentStat: unsubscribeCount,
        statType: emailStatBoxTypes.UNSUBSCRIBED
      }
    ];
    return statBoxesEmail;
  };

  const getSmsStats = campaignReportData => {
    let statBoxesEmail = [
      {
        currentStat:
          campaignReportData?.totalMetrics?.undeliveredMetrics?.totalAccepted
            ?.totalCount,
        statType: smsStatBoxTypes.ACCEPTED
      },
      {
        currentStat:
          campaignReportData?.totalMetrics?.totalRequested?.totalCount,
        statType: smsStatBoxTypes.SENT
      },
      {
        currentStat:
          campaignReportData?.totalMetrics?.totalDelivered?.totalCount,
        statType: smsStatBoxTypes.DELIVERED
      },
      {
        currentStat:
          campaignReportData?.totalMetrics?.undeliveredMetrics?.totalFailed
            ?.totalCount,
        statType: smsStatBoxTypes.ERRORED
      }
    ];
    return statBoxesEmail;
  };

  const getEventValues = (identifierValues, messageId) => {
    let eventData = Object.keys(identifierValues)
      .sort()
      .reduce(function(result, key) {
        result[key] = identifierValues[key];
        return result;
      }, {});
    return { ...eventData, messageId: messageId };
  };

  const setCampaignEmailReportDetails = details => {
    let tempData = [...details];
    tempData = tempData.map(email => {
      let tempEmail = {
        toAddress: email?.emailAddress || '-',
        eventIdentifierValues: getEventValues(
          email?.eventIdentifierValues || {},
          email?.eventHistory?.messageId
        ),
        requestDate: '',
        status: [],
        errorReason: null
      };

      email['eventHistory']['results'].map(message => {
        tempEmail.eventIdentifierValues = {
          ...tempEmail.eventIdentifierValues,
          type: message?.resultType,
          errorReason:
            (message.errorReason && !tempEmail['errorReason']
              ? message.errorReason
              : tempEmail.errorReason) || '-'
        };
        tempEmail.requestDate = message?.date
          ? convertToDateTimeFormat2OnlyString(message.date)
          : '-';
        tempEmail.status.push(message.success);
      });
      if (tempEmail.status.every(b => b === false)) {
        tempEmail.status = 'FAILURE';
      } else if (tempEmail.status.every(b => b === true)) {
        tempEmail.status = 'SUCCESS';
      } else {
        tempEmail.status = 'INCOMPLETE';
      }
      return tempEmail;
    });
    return tempData;
  };

  const onBlur = e => {
    formController.onBlur(e);
    validate();
  };

  const onFocus = e => {
    formController.onFocus(e);
    validate();
  };

  const shouldShowError = name => {
    return formController.shouldShowError(queryDataErrors, name);
  };

  const getErrorMessage = name => {
    return formController.getErrorMessage(queryDataErrors, name);
  };

  const onClickDownloadCsv = () => {
    let payload = { ...queryData, type: type, providerMetric: providerMetric };
    dispatch(getCampaignReportCsvFile(payload));
  };

  return {
    reportData,
    campaignData,
    campaignDataCount,
    queryData,
    isLoading,
    handleDateChange,
    updateParams,
    getEmailStats,
    getSmsStats,
    unsubscribeCount,
    onBlur,
    onFocus,
    queryDataErrors,
    shouldShowError,
    getErrorMessage,
    error,
    onClickDownloadCsv,
    isDownloading,
    gettingFileSucceeded,
    getMaxAvailableDailyDate,
    getMinAvailableDailyDate,
    getMaxAvailableMonthlyDate,
    getMinAvailableMonthlyDate,
    getPresetRange,
    getMatchingPreset,
    applyFilters,
    dailyReportDelayHours,
    monthlyReportDelayHours,
    dailyRuntime,
    monthlyRuntime,
    dailyRangeDays,
    monthlyRangeMonths
  };
}
